import { Box } from '@mui/material'
import { DataGridPremiumProps, GridPinnedColumnFields } from '@mui/x-data-grid-premium'
import { ReactNode } from 'react'
import { DataGridBase, DataGridBaseProps, TableEditMode } from '../data-grid/DataGridBase'
import { ControlledTableFilterPanel } from './components/ControlledTableFilterPanel'
import { ControlledTableToolbar, ExportOptions } from './components/ControlledTableToolbar'

type GridProps = Pick<
  DataGridBaseProps,
  | 'apiRef'
  | 'pinnedColumns'
  | 'hasUncontainedTableStyling'
  | 'rows'
  | 'columns'
  | 'loading'
  | 'onRowClick'
  | 'getDefaultRowLink'
  | 'processRowUpdate'
  | 'paginationMode'
  | 'sortingMode'
  | 'filterMode'
  | 'TableEmptyState'
  | 'onPinnedColumnsChange'
  | 'groupingColDef'
  | 'unstable_dataSource'
  | 'checkboxSelection'
  | 'rowSelectionModel'
  | 'onRowSelectionModelChange'
  | 'getRowHeight'
>

type ControlledGridProps = Required<
  Pick<
    DataGridPremiumProps,
    | 'filterModel'
    | 'onFilterModelChange'
    | 'sortModel'
    | 'onSortModelChange'
    | 'paginationModel'
    | 'onPaginationModelChange'
    | 'columnVisibilityModel'
    | 'onColumnVisibilityModelChange'
  >
>

export interface ControlledTableProps extends GridProps, ControlledGridProps {
  tableName: string
  exportOptions: ExportOptions
  totalCount?: number
  totalCountLoading?: boolean
  totalCountIsApprox?: boolean
  hideRowGrouping?: boolean
  hideSaveFilterButton?: boolean
  hideFooter?: boolean
  customFooter?: ReactNode
  hideSearchBar?: boolean
  hideFilterPanel?: boolean
  disableExportInToolbar?: boolean
  exportLoading?: boolean
  onExportClick?: () => void
  toolbarHeader?: ReactNode
  editMode?: TableEditMode
  pagination?: boolean
  disableColumnFilter?: boolean
  disableColumnSelector?: boolean
  allowPageScroll?: boolean
  initialState?: {
    pinnedColumns?: GridPinnedColumnFields
    rowGrouping?: {
      model: string[]
    }
  }
  pinnedColumns?: GridPinnedColumnFields
  onPinnedColumnsChange?: (newPinnedColumns: GridPinnedColumnFields) => void
  disableColumnMenu?: boolean
  groupingColDef?: {
    leafField: string
  }
  pageSizeOptions?: number[]
  hasExternalHeader?: boolean
}

export const ControlledTable: React.FC<ControlledTableProps> = ({
  tableName,
  exportOptions,
  totalCount,
  totalCountLoading = false,
  totalCountIsApprox = false,
  hideRowGrouping = false,
  hideSaveFilterButton = false,
  hideFooter = false,
  customFooter,
  hideSearchBar = false,
  hideFilterPanel = false,
  disableExportInToolbar = false,
  exportLoading = false,
  onExportClick,
  pagination = true,
  allowPageScroll = false,
  toolbarHeader,
  editMode,
  initialState,
  pinnedColumns,
  onPinnedColumnsChange,
  pageSizeOptions,
  hasExternalHeader = false,
  ...gridProps
}) => {
  return (
    <Box
      sx={{
        width: '100%',
        overflow: 'hidden',
        ...(customFooter != null && {
          '& .MuiDataGrid-footerContainer': {
            maxHeight: 'none',
            background: 'transparent',
            boxShadow: 'none',
          },
        }),
      }}
      data-testid="controlled-table"
    >
      <DataGridBase
        stateKey={tableName}
        apiRef={gridProps.apiRef}
        allowPageScroll={allowPageScroll}
        {...gridProps}
        hideFooter={customFooter != null ? false : hideFooter}
        pagination={pagination}
        editMode={editMode}
        rowCount={totalCount}
        totalCountLoading={totalCountLoading}
        totalCountIsApprox={totalCountIsApprox}
        initialState={initialState}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={onPinnedColumnsChange}
        hasExternalHeader={hasExternalHeader}
        slots={{
          toolbar: ControlledTableToolbar,
          ...(hideFilterPanel
            ? {}
            : {
                filterPanel: () => <ControlledTableFilterPanel hideSaveButton={hideSaveFilterButton} />,
              }),
          ...(customFooter != null && { footer: () => customFooter }),
        }}
        slotProps={{
          toolbar: {
            tableName,
            exportOptions,
            displayRowGroupingButton: !hideRowGrouping,
            hideSearchBar,
            disableExportInToolbar,
            exportLoading,
            onExportClick,
            header: toolbarHeader,
            isToolbarEmpty:
              hideSearchBar && disableExportInToolbar && gridProps.disableColumnFilter && gridProps.disableColumnSelector,
          },
        }}
        unstable_dataSource={gridProps.unstable_dataSource}
        pageSizeOptions={pageSizeOptions}
      />
    </Box>
  )
}
